/**
 * TourProvider — the brain of "Maya's Glide".
 *
 * Owns:
 *   • Which beacon is currently open (or null).
 *   • Which beacons the customer has already seen (persisted to
 *     localStorage so we don't re-bother returning customers).
 *   • The auto-walk sequence (first-visit linear walkthrough).
 *   • The tour copy dictionary, language-keyed (English at launch,
 *     Hindi + Hinglish in Wave 2).
 *   • A non-blocking analytics fire that records every interaction.
 *
 * Composition:
 *   <TourProvider>
 *     ...your app...
 *     <Spotlight />     ← reads openBeaconId, renders backdrop
 *     <MayaBubble />    ← reads openBeaconId, renders Maya's tip
 *   </TourProvider>
 *
 * The provider mounts <Spotlight /> and <MayaBubble /> automatically
 * so consumers only need to drop <Beacon id="…" /> next to elements.
 */
import { createContext, useCallback, useEffect, useMemo, useRef, useState } from "react";

import enCopy from "./tourCopy.en.json";
import hiCopy from "./tourCopy.hi.json";
import hiEnCopy from "./tourCopy.hi-en.json";
import Spotlight from "./Spotlight";
import MayaBubble from "./MayaBubble";
import { fireTourEvent } from "./tourAnalytics";

export const TourContext = createContext(null);

const SEEN_KEY = "jns_tour_seen_v1";
const LANG_KEY = "jns_tour_lang";
// Jun 28 2026 — once TRUE, no screen auto-opens its tour again. Each screen
// used to auto-walk on its own first visit, so a first-time customer was
// interrupted at Step 0, the photo step, the customiser AND the preview —
// four pop-ups on one journey. We now auto-open at most ONCE (the first
// screen they land on); every later beacon stays available on tap (the
// "Tour" / "?" pills) but never interrupts the glide again.
const AUTOWALK_KEY = "jns_tour_autowalked_v1";
const COPY_BY_LANG = { en: enCopy, hi: hiCopy, "hi-en": hiEnCopy };
export const TOUR_LANGUAGES = [
    { code: "en", label: "English", short: "EN" },
    { code: "hi-en", label: "Hinglish", short: "HI-EN" },
    { code: "hi", label: "हिन्दी", short: "हिं" },
];

function detectInitialLanguage(explicit) {
    if (explicit && COPY_BY_LANG[explicit]) return explicit;
    if (typeof window === "undefined") return "en";
    // 1. Saved customer preference (localStorage)
    try {
        const saved = window.localStorage.getItem(LANG_KEY);
        if (saved && COPY_BY_LANG[saved]) return saved;
    } catch { /* ignore */ }
    // 2. URL query (?lang=hi or ?lang=hi-en) — handy for support links.
    try {
        const q = new URLSearchParams(window.location.search).get("lang");
        if (q && COPY_BY_LANG[q]) return q;
    } catch { /* ignore */ }
    // 3. Browser language. Hindi-detecting browsers get Hinglish by
    // default (the most natural fit for our customer base), not pure
    // Hindi — that's reserved for explicit opt-in.
    try {
        const browser = (navigator.language || "en").toLowerCase();
        if (browser.startsWith("hi")) return "hi-en";
    } catch { /* ignore */ }
    return "en";
}

function loadSeen() {
    if (typeof window === "undefined") return new Set();
    try {
        const raw = window.localStorage.getItem(SEEN_KEY);
        if (!raw) return new Set();
        const parsed = JSON.parse(raw);
        return new Set(Array.isArray(parsed) ? parsed : []);
    } catch {
        return new Set();
    }
}

function saveSeen(set) {
    if (typeof window === "undefined") return;
    try {
        window.localStorage.setItem(SEEN_KEY, JSON.stringify(Array.from(set)));
    } catch { /* ignore */ }
}

export default function TourProvider({ children, language: explicitLanguage = null }) {
    const [openBeaconId, setOpenBeaconId] = useState(null);
    // Auto-walk state: when set, "Got it" advances through the sequence.
    const [walkSequence, setWalkSequence] = useState(null);
    const [walkIndex, setWalkIndex] = useState(0);
    const seenRef = useRef(loadSeen());
    // Whether ANY auto-walk has already fired for this customer (persisted).
    const autoWalkedRef = useRef((() => {
        if (typeof window === "undefined") return false;
        try { return window.localStorage.getItem(AUTOWALK_KEY) === "1"; } catch { return false; }
    })());
    // iter358 — Language state (default en, persisted to localStorage).
    const [language, setLanguageState] = useState(() => detectInitialLanguage(explicitLanguage));
    // Force re-render when seen set mutates. (Set identity is reused so
    // we bump a counter to nudge React.)
    const [, bump] = useState(0);

    const copy = COPY_BY_LANG[language] || enCopy;

    const setLanguage = useCallback((next) => {
        if (!COPY_BY_LANG[next]) return;
        setLanguageState(next);
        try {
            if (typeof window !== "undefined") {
                window.localStorage.setItem(LANG_KEY, next);
            }
        } catch { /* ignore */ }
        fireTourEvent("language_changed", "__lang__", { to: next });
    }, []);

    const persistSeen = useCallback(() => {
        saveSeen(seenRef.current);
        bump((n) => n + 1);
    }, []);

    const hasBeenSeen = useCallback((id) => seenRef.current.has(id), []);

    const markSeen = useCallback((id) => {
        if (!seenRef.current.has(id)) {
            seenRef.current.add(id);
            persistSeen();
        }
    }, [persistSeen]);

    const openBeacon = useCallback((id, { sourceWalk = false } = {}) => {
        setOpenBeaconId(id);
        fireTourEvent("opened", id, { source: sourceWalk ? "walk" : "manual" });
    }, []);

    const closeBeacon = useCallback(() => {
        if (openBeaconId) {
            markSeen(openBeaconId);
            fireTourEvent("dismissed", openBeaconId);
        }
        setOpenBeaconId(null);
        // Closing in the middle of a walk ends the walk.
        if (walkSequence) {
            fireTourEvent("walk_dismissed", walkSequence[walkIndex] || "", {
                step_index: walkIndex,
            });
            setWalkSequence(null);
            setWalkIndex(0);
        }
    }, [openBeaconId, markSeen, walkSequence, walkIndex]);

    const next = useCallback(() => {
        if (openBeaconId) {
            markSeen(openBeaconId);
            fireTourEvent("completed", openBeaconId);
        }
        if (!walkSequence) {
            setOpenBeaconId(null);
            return;
        }
        const nextIdx = walkIndex + 1;
        if (nextIdx >= walkSequence.length) {
            // Walk complete — celebration moment.
            fireTourEvent("walk_completed", walkSequence.join(","), {
                length: walkSequence.length,
            });
            setOpenBeaconId("__celebration__");
            setWalkSequence(null);
            setWalkIndex(0);
            return;
        }
        setWalkIndex(nextIdx);
        const nextId = walkSequence[nextIdx];
        // Smooth transition — bubble fades out, next bubble fades in.
        setOpenBeaconId(null);
        setTimeout(() => {
            setOpenBeaconId(nextId);
            fireTourEvent("opened", nextId, { source: "walk", step_index: nextIdx });
        }, 220);
    }, [openBeaconId, walkSequence, walkIndex, markSeen]);

    const skip = useCallback(() => {
        if (openBeaconId) {
            fireTourEvent("skipped", openBeaconId, {
                step_index: walkIndex,
                via_walk: !!walkSequence,
            });
        }
        // Mark all remaining walk beacons as seen so we don't re-prompt.
        if (walkSequence) {
            for (let i = walkIndex; i < walkSequence.length; i += 1) {
                seenRef.current.add(walkSequence[i]);
            }
            persistSeen();
        }
        setOpenBeaconId(null);
        setWalkSequence(null);
        setWalkIndex(0);
    }, [openBeaconId, walkSequence, walkIndex, persistSeen]);

    const hasAutoWalked = useCallback(() => autoWalkedRef.current, []);

    const startAutoWalk = useCallback((sequence, { force = false } = {}) => {
        if (!Array.isArray(sequence) || !sequence.length) return;
        if (!force) {
            const allSeen = sequence.every((id) => seenRef.current.has(id));
            if (allSeen) return;
        }
        // Record that the customer has now experienced an auto-walk, so no
        // later screen auto-opens its own tour. Manual re-triggers (force,
        // e.g. the "Tour" pill) still work — they bypass the useAutoWalk gate.
        if (!autoWalkedRef.current) {
            autoWalkedRef.current = true;
            try {
                if (typeof window !== "undefined") window.localStorage.setItem(AUTOWALK_KEY, "1");
            } catch { /* ignore */ }
        }
        setWalkSequence(sequence);
        setWalkIndex(0);
        setOpenBeaconId(sequence[0]);
        fireTourEvent("walk_started", sequence.join(","), {
            length: sequence.length, forced: !!force,
        });
    }, []);

    const getCopy = useCallback((id) => {
        if (id === "__celebration__") return copy.celebration;
        return copy[id] || null;
    }, [copy]);

    // ESC key closes the active beacon — keyboard a11y.
    useEffect(() => {
        if (!openBeaconId) return undefined;
        const onKey = (e) => {
            if (e.key === "Escape") closeBeacon();
            else if (e.key === "Enter" && walkSequence) next();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [openBeaconId, closeBeacon, next, walkSequence]);

    const value = useMemo(() => ({
        openBeaconId,
        seenBeacons: seenRef.current,
        language,
        availableLanguages: TOUR_LANGUAGES,
        setLanguage,
        openBeacon,
        closeBeacon,
        next,
        skip,
        startAutoWalk,
        hasBeenSeen,
        hasAutoWalked,
        getCopy,
        isAutoWalk: !!walkSequence,
        walkIndex,
        walkSequence,
    }), [
        openBeaconId, language, setLanguage, openBeacon, closeBeacon, next, skip,
        startAutoWalk, hasBeenSeen, hasAutoWalked, getCopy, walkSequence, walkIndex,
    ]);

    return (
        <TourContext.Provider value={value}>
            {children}
            <Spotlight />
            <MayaBubble />
        </TourContext.Provider>
    );
}
